import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useListingRestrictions from "../../data/useListingRestrictions";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../components/notifications/useNotifications";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import PopOutBox from "../../components/shared/PopOutBox";
import Card from "../../components/shared/Card/Card";
import SelectableTable from "../../components/shared/Tables/SelectableTable";
import Modal from "../../layouts/Modal";
import {
  CreateUpdateListingRestrictionRuleRequest,
  IListingRestrictionRule,
  ListingRestrictionAction,
  listingRestrictionActionLabel,
} from "../../models/ListingRestriction";
import { Marketplace, marketplaceLabel } from "../../models/Publishing";

const emptyRule: CreateUpdateListingRestrictionRuleRequest = {
  name: "",
  marketplace: Marketplace.Ebay,
  action: ListingRestrictionAction.Block,
  keywords: "",
  excludeKeywords: "",
  reason: "",
  isActive: true,
  categoryIds: [],
};

const selectClasses =
  "w-full mt-1 text-gray-900 border-1 border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 px-3 py-2 text-sm";

export default function ListingRestrictions() {
  const { setInfo } = usePageNameContext();

  useEffect(() => {
    setInfo({
      name: "Listing Restrictions",
      desc: "Keep parts a marketplace won't accept, such as airbags on eBay, from being listed there",
    });
  }, []);

  return (
    <div className="mt-4">
      <RulesTab />
    </div>
  );
}

function RulesTab() {
  const [selected, setSelected] = useState<IListingRestrictionRule | undefined>(undefined);
  const { listingRestrictions, create, update, remove } = useListingRestrictions();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Listing Restriction");
  const { addNotification } = useNotification();
  const { register, handleSubmit, reset, setValue, watch } = useForm<CreateUpdateListingRestrictionRuleRequest>({
    defaultValues: emptyRule,
  });

  const { categories: allCategories } = useCategoriesPartial();
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [selectedCategoryRows, setSelectedCategoryRows] = useState<string[]>([]);
  const formCategoryIds = watch("categoryIds");

  useEffect(() => {
    if (selected) {
      reset({
        name: selected.name ?? "",
        marketplace: selected.marketplace,
        action: selected.action,
        keywords: selected.keywords ?? "",
        excludeKeywords: selected.excludeKeywords ?? "",
        reason: selected.reason ?? "",
        isActive: selected.isActive,
        categoryIds: selected.categories?.map((c) => c.partCategoryId) ?? [],
      });
    } else {
      reset(emptyRule);
    }
  }, [selected]);

  const onSubmit = (data: CreateUpdateListingRestrictionRuleRequest) => {
    // Selects hand back strings
    const req = { ...data, marketplace: Number(data.marketplace), action: Number(data.action) };

    if (!req.name?.trim() || (!req.keywords?.trim() && (req.categoryIds?.length ?? 0) === 0)) {
      addNotification({
        variant: "error",
        primaryText: "Give the restriction a name and at least one category or keyword",
      });
      return;
    }

    if (selected) {
      update.mutate({ id: selected.id, req }, {
        ...saveHandlers,
        onSettled: () => reset(req),
      });
    } else {
      create.mutate(req, {
        ...saveHandlers,
        onSuccess() {
          saveHandlers.onSuccess();
          reset(emptyRule);
        },
      });
    }
  };

  const openCategoriesModal = () => {
    setSelectedCategoryRows([...(formCategoryIds ?? [])]);
    setCategoriesModalOpen(true);
  };

  const applyCategories = () => {
    setValue("categoryIds", selectedCategoryRows);
    setCategoriesModalOpen(false);
  };

  const onDelete = (rule: IListingRestrictionRule) => {
    remove.mutate(rule.id, deleteHandlers);
    setSelected(undefined);
  };

  if (listingRestrictions.isLoading) return <LoadingWheel />;

  return (
    <>
      <Modal open={categoriesModalOpen} setOpen={setCategoriesModalOpen} width="sm:max-w-3xl">
        <div className="sm:flex sm:items-start">
          <div className="mt-3 text-left sm:mt-0 sm:ml-2 sm:text-left w-full">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Select Categories</h3>
            <SelectableTable
              options={(allCategories.data?.data ?? []).map(c => ({ id: c.id, name: c.name }))}
              isLoading={allCategories.isLoading}
              selectedIds={selectedCategoryRows}
              setSelectedIds={setSelectedCategoryRows}
              placeholder="Search categories"
            />
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <ButtonInput label={"Apply Selected"} isSubmit={false} onClick={applyCategories} classes="sm:ml-3 sm:w-auto w-full px-4" />
          <ButtonInput label={"Cancel"} isSubmit={false} onClick={() => setCategoriesModalOpen(false)} classes="mt-3 sm:mt-0 sm:w-auto w-full bg-white !text-gray-700 ring-1 ring-inset ring-gray-300 hover:!bg-gray-50 px-4" />
        </div>
      </Modal>

      <div className="mb-4 text-gray-600">
        <p>
          A part matches a restriction if it is in one of its categories, or if a keyword appears in its title
          and no exclude keyword does. Blocked parts are never sent to that marketplace and, for eBay, are hidden
          from the Unlisted table. Warnings still list the part, but flag it on the publish results.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        <Card title="Restrictions">
          <div className="min-h-[25vh]">
            <button
              className={"w-full text-left px-3 py-2 hover:bg-gray-100"}
              onClick={() => setSelected(undefined)}
            >
              + Create New Restriction
            </button>

            {listingRestrictions.data?.data?.map((r) => (
              <button
                key={r.id}
                className={"w-full text-left px-3 py-2 hover:bg-gray-100" + (selected?.id === r.id ? " bg-blue-50" : "")}
                onClick={() => setSelected(r)}
              >
                <span className={r.isActive ? "" : "text-gray-400 line-through"}>{r.name}</span>
                <span className="ml-2 text-xs text-gray-500">
                  {listingRestrictionActionLabel[r.action as ListingRestrictionAction]} · {marketplaceLabel[r.marketplace as Marketplace]}
                </span>
              </button>
            ))}
          </div>
        </Card>

        <Card title="Restriction Details" containerClassName="lg:col-span-2">
          <form className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={handleSubmit(onSubmit)}>

            <TextInput label="Name" register={register} registerName="name" cols="sm:col-span-2" />

            <div className="sm:col-span-1">
              <label className="text-sm font-medium text-gray-500">Marketplace</label>
              <select className={selectClasses} {...register("marketplace")}>
                {[Marketplace.Ebay, Marketplace.Shopify, Marketplace.PartPro].map((m) => (
                  <option key={m} value={m}>{marketplaceLabel[m]}</option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-1">
              <label className="text-sm font-medium text-gray-500 flex items-center space-x-1">
                <span>Action</span>
                <PopOutBox classes="ml-1 p-2 w-64">
                  <div className="text-xs text-gray-700">
                    Block: the part is never sent to this marketplace. Warn: the part is listed, with a warning on the publish results.
                  </div>
                </PopOutBox>
              </label>
              <select className={selectClasses} {...register("action")}>
                {[ListingRestrictionAction.Block, ListingRestrictionAction.Warn].map((a) => (
                  <option key={a} value={a}>{listingRestrictionActionLabel[a]}</option>
                ))}
              </select>
            </div>

            <TextInput
              label="Keywords (comma or new line separated)"
              register={register}
              registerName="keywords"
              richText={true}
              rows={3}
              cols="sm:col-span-1"
            />

            <TextInput
              label="Exclude keywords"
              register={register}
              registerName="excludeKeywords"
              richText={true}
              rows={3}
              cols="sm:col-span-1"
            />

            <div className="sm:col-span-2 -mt-2 text-xs text-gray-500">
              Keywords match anywhere in the part's titles, ignoring case - "airbag" also matches "Airbags". An exclude
              keyword such as "switch" stops "Airbag On/Off Switch" matching. Exclude keywords don't affect categories.
            </div>

            <TextInput label="Reason (shown when a part is stopped or warned)" register={register} registerName="reason" cols="sm:col-span-2" />

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-700">Categories</div>
                  <div className="text-xs text-gray-500">{formCategoryIds?.length ?? 0} selected</div>
                </div>
                <ButtonInput label={"Edit Categories"} isSubmit={false} onClick={openCategoriesModal} classes="" />
              </div>
            </div>

            <div className="flex items-center gap-x-4">
              <label className="text-sm font-medium text-gray-700">Active</label>
              <input type="checkbox" {...register("isActive")} />
            </div>

            <div className="sm:col-span-2 flex items-center justify-between">
              <ButtonInput label={selected ? "Save" : "Create"} isSubmit={true} onClick={undefined} classes="" />

              {selected && (
                <ButtonInput label={"Delete"} isSubmit={false} onClick={() => onDelete(selected)} classes="bg-red-600 hover:bg-red-700" />
              )}
            </div>

          </form>
        </Card>
      </div>
    </>
  );
}
